import React from 'react';
import register from './img/register.svg';
import content from './img/content.svg';
import notification from './img/notification.svg';
import date from './img/date.svg';
import select from './img/select.svg';
import add from './img/add.svg';
import data from './img/data.svg';
import project from './img/project.svg';
import processed from './img/processed.svg';
import discipline from './img/discipline.svg';
import disapproved from './img/disapproved.svg';
import selectCard from './img/selectCard.svg';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

const emptyStateVariants = cva('cursor-default text-neutral text-center text-sm w-[360px] max-w-full mx-auto p-5 rounded-xl', {
  variants: {
    variant: {
      default: 'bg-white shadow-dropshadow-sm',
      plain: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

function getImage(type) {
  switch (type) {
    case 'register':
      return register;

    case 'content':
      return content;

    case 'notification':
      return notification;

    case 'date':
      return date;

    case 'select':
      return select;

    case 'add':
      return add;

    case 'data':
      return data;

    case 'project':
      return project;

    case 'processed':
      return processed;

    case 'discipline':
      return discipline;

    case 'disapproved':
      return disapproved;

    case 'selectCard':
      return selectCard;

    default:
      break;
  }
}

function getText(type) {
  const { t } = useTranslation();

  switch (type) {
    case 'register':
      return t('emptyState.textRegister');

    case 'content':
      return t('emptyState.textContent');

    case 'notification':
      return t('emptyState.textNotification');

    case 'date':
      return t('emptyState.textDate');

    case 'select':
      return t('emptyState.textSelect');

    case 'add':
      return t('emptyState.textAdd');

    case 'data':
      return t('emptyState.textData');

    case 'project':
      return t('emptyState.textProject');

    case 'processed':
      return t('emptyState.textProcessed');

    case 'discipline':
      return t('emptyState.textDiscipline');

    default:
      return t('emptyState.textDefault');
  }
}

const EmptyState = React.forwardRef(({ type, title, text, cta, className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-empty-state', emptyStateVariants({ variant, className }))}
    {...props}
  >
    {type && <EmptyStateIcon type={type} />}

    {title && <EmptyStateTitle>{title}</EmptyStateTitle>}

    <EmptyStateText>{text || getText(type)}</EmptyStateText>

    {cta && (
      <Button
        variant="outline"
        size="sm"
        wide
        className="mt-2"
        onClick={cta?.onClick}
      >
        {cta?.text}
      </Button>
    )}
  </div>
));
EmptyState.displayName = 'EmptyState';

const EmptyStateTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('text-neutral text-base leading-normal mb-0', className)}
    {...props}
  />
));
EmptyStateTitle.displayName = 'EmptyStateTitle';

const EmptyStateText = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-base leading-normal mb-0 [&_a]:font-bold [&_a]:text-neutral-700 [&_a]:underline', className)}
    {...props}
  />
));
EmptyStateText.displayName = 'EmptyStateText';

const EmptyStateIcon = React.forwardRef(({ className, type, ...props }, ref) => (
  <img
    ref={ref}
    className={cn('mb-4 w-[120px]', className)}
    alt=""
    src={getImage(type)}
    {...props}
  />
));
EmptyStateIcon.displayName = 'EmptyStateIcon';

export { EmptyState, EmptyStateTitle, EmptyStateText, EmptyStateIcon };
